import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useInsertName } from "./utils/helpers";

const TYPING_SPEED = 35;
const PROMPT_PAUSE = 400;
const LINE_PAUSE = 180;

function useTypewriter(lines, startDelay = 500) {
  const [visibleLines, setVisibleLines] = useState([]);
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (lineIdx >= lines.length) {
      setDone(true);
      return;
    }

    const line = lines[lineIdx];

    if (line.type === "prompt") {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => [...prev, { ...line, displayText: line.text }]);
        setLineIdx((i) => i + 1);
        setCharIdx(0);
      }, lineIdx === 0 ? startDelay : PROMPT_PAUSE);
      return () => clearTimeout(timer);
    }

    if (charIdx === 0) {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => [...prev, { ...line, displayText: "" }]);
        setCharIdx(1);
      }, LINE_PAUSE);
      return () => clearTimeout(timer);
    }

    if (charIdx <= line.text.length) {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...line, displayText: line.text.slice(0, charIdx) };
          return updated;
        });
        setCharIdx((c) => c + 1);
      }, TYPING_SPEED);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setLineIdx((i) => i + 1);
      setCharIdx(0);
    }, LINE_PAUSE);
    return () => clearTimeout(timer);
  }, [lines, lineIdx, charIdx, startDelay]);

  const isTyping = lineIdx < lines.length;
  return { visibleLines, isTyping, done };
}

const COMMANDS = {
  help: [
    { type: "response", text: "> verfügbare befehle:" },
    { type: "response", text: ">   help          – diese hilfe anzeigen" },
    { type: "response", text: ">   ls            – seiten auflisten" },
    { type: "response", text: ">   cd <pfad>     – zu einer seite navigieren" },
    { type: "response", text: ">   back          – zurück zur letzten seite" },
    { type: "response", text: ">   clear         – terminal leeren" },
  ],
  ls: [
    { type: "response", text: "> /             – startseite" },
    { type: "response", text: "> /zenlab       – zenlab" },
    { type: "response", text: "> /blog         – blog" },
    { type: "response", text: "> /courses      – kurse" },
    { type: "response", text: "> /contact      – kontakt" },
    { type: "response", text: "> /about        – über mich" },
  ],
};

const NAV_PATHS = {
  "/": "/",
  "~": "/",
  "/zenlab": "/zenlab",
  "zenlab": "/zenlab",
  "/blog": "/blog",
  "blog": "/blog",
  "/courses": "/courses",
  "courses": "/courses",
  "/contact": "/contact",
  "contact": "/contact",
  "/about": "/about",
  "about": "/about",
};

const SHORTCUTS = [
  { key: "a", label: "Start", path: "/" },
  { key: "b", label: "SagHallo", path: "/contact" },
  { key: "c", label: "ZenLab", path: "/zenlab" },
];

const NotFound = () => {
  const name = useInsertName();
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [commandHistory, setCommandHistory] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [historyIndex, setHistoryIndex] = useState(-1);

  const bootLines = [
    { type: "prompt", text: "$ cd /zenlab" },
    { type: "prompt", text: `$ open ${location.pathname}` },
    { type: "response", text: "> resolving path..." },
    { type: "error", text: `> ERROR 404: "${location.pathname}" nicht gefunden` },
    { type: "error", text: "> die adresse existiert nicht oder wurde verschoben." },
    { type: "response", text: `> hey${name ? ` ${name}` : ""}, tipp "help" oder drück [a] [b] [c]` },
    { type: "status", text: "> exit code 1" },
  ];

  const { visibleLines: bootVisibleLines, isTyping, done } = useTypewriter(bootLines);
  const [interactiveLines, setInteractiveLines] = useState([]);

  const allLines = [...bootVisibleLines, ...interactiveLines];

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [allLines]);

  useEffect(() => {
    if (done && inputRef.current) {
      inputRef.current.focus();
    }
  }, [done]);

  const handleCommand = useCallback((cmd) => {
    const trimmed = cmd.trim().toLowerCase();
    const promptLine = { type: "prompt", displayText: `$ ${cmd.trim()}` };

    if (!trimmed) return;

    setCommandHistory((prev) => [...prev, cmd.trim()]);
    setHistoryIndex(-1);

    if (trimmed === "clear") {
      setInteractiveLines([]);
      return;
    }

    if (trimmed === "back") {
      setInteractiveLines((prev) => [
        ...prev,
        promptLine,
        { type: "response", displayText: "> navigiere zurück..." },
      ]);
      setTimeout(() => navigate(-1), 600);
      return;
    }

    if (COMMANDS[trimmed]) {
      setInteractiveLines((prev) => [
        ...prev,
        promptLine,
        ...COMMANDS[trimmed].map((l) => ({ ...l, displayText: l.text })),
      ]);
      return;
    }

    // cd <path>
    const cdMatch = trimmed.match(/^cd\s+(.+)$/);
    if (cdMatch) {
      const target = cdMatch[1];
      const navPath = NAV_PATHS[target];
      if (navPath) {
        setInteractiveLines((prev) => [
          ...prev,
          promptLine,
          { type: "status", displayText: `> navigiere zu ${navPath}...` },
        ]);
        setTimeout(() => navigate(navPath), 600);
        return;
      }
      setInteractiveLines((prev) => [
        ...prev,
        promptLine,
        { type: "error", displayText: `> pfad "${target}" nicht gefunden. tipp "ls" für verfügbare seiten.` },
      ]);
      return;
    }

    // Shortcut: direkt ein bekannter Pfad
    if (NAV_PATHS[trimmed] || NAV_PATHS[`/${trimmed}`]) {
      const navPath = NAV_PATHS[trimmed] || NAV_PATHS[`/${trimmed}`];
      setInteractiveLines((prev) => [
        ...prev,
        promptLine,
        { type: "status", displayText: `> navigiere zu ${navPath}...` },
      ]);
      setTimeout(() => navigate(navPath), 600);
      return;
    }

    setInteractiveLines((prev) => [
      ...prev,
      promptLine,
      { type: "error", displayText: `> command not found: ${cmd.trim()}` },
      { type: "response", displayText: `> tipp "help" für verfügbare befehle.` },
    ]);
  }, [navigate]);

  const handleShortcut = useCallback((shortcut) => {
    const promptLine = { type: "prompt", displayText: `$ cd ${shortcut.path}` };
    setInteractiveLines((prev) => [
      ...prev,
      promptLine,
      { type: "status", displayText: `> navigiere zu ${shortcut.path}...` },
    ]);
    setTimeout(() => navigate(shortcut.path), 600);
  }, [navigate]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCommand(inputValue);
      setInputValue("");
      return;
    }

    // Shortcut-Keys a, b, c nur wenn Input leer
    if (!inputValue && done) {
      const shortcut = SHORTCUTS.find((s) => s.key === e.key.toLowerCase());
      if (shortcut) {
        e.preventDefault();
        handleShortcut(shortcut);
        return;
      }
    }

    // Pfeil hoch/runter für Command-History
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (commandHistory.length === 0) return;
      const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(newIndex);
      setInputValue(commandHistory[newIndex]);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex === -1) return;
      const newIndex = historyIndex + 1;
      if (newIndex >= commandHistory.length) {
        setHistoryIndex(-1);
        setInputValue("");
      } else {
        setHistoryIndex(newIndex);
        setInputValue(commandHistory[newIndex]);
      }
    }
  };

  const focusInput = () => {
    if (done && inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 font-mono"
      style={{ background: "linear-gradient(180deg, #121212, #151515)" }}
    >
      <div className="w-full max-w-2xl mx-auto">
        <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40">

          {/* Title Bar */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" />
            <span className="ml-3 text-xs text-[#AC8E66] select-none">zenlab://terminal</span>
          </div>

          {/* Terminal Content */}
          <div
            ref={containerRef}
            className="px-4 sm:px-6 py-5 sm:py-6 bg-[#151515] 
            min-h-[220px] max-h-[60vh] overflow-y-auto cursor-text"
            onClick={focusInput}
          >
            {allLines.map((line, i) => {
              const showCursor = isTyping && i === bootVisibleLines.length - 1 && line.type !== "prompt";
              const cursor = showCursor
                ? <span className="inline-block w-[2px] h-[1em] bg-[#AC8E66] ml-[1px] align-middle animate-cursor-blink" />
                : null;

              if (line.type === "prompt") {
                return (
                  <p key={i} className="text-sm sm:text-sm text-[#EFEBDC]/60 mt-3 first:mt-0">
                    <span className="text-[#AC8E66]">$</span>
                    {line.displayText.slice(1)}
                  </p>
                );
              }

              if (line.type === "error") {
                return (
                  <p key={i} className="text-sm sm:text-sm text-[#ff6b6b] mt-1 flex items-start gap-2">
                    <span className="text-[#ff6b6b]/60 flex-none">&gt;</span>
                    <span className="min-w-0 flex-1">
                      {line.displayText.slice(1)}
                      {cursor}
                    </span>
                  </p>
                );
              }

              if (line.type === "status") {
                return (
                  <p key={i} className="text-sm sm:text-sm text-orange-400 mt-1">
                    <span className="text-orange-400/60">&gt;</span>
                    {line.displayText.slice(1)}
                    {cursor}
                  </p>
                );
              }

              return (
                <p key={i} className="text-sm sm:text-sm text-[#EFEBDC]/80 mt-1 flex items-start gap-2">
                  <span className="text-[#AC8E66]/60 flex-none">&gt;</span>
                  <span className="min-w-0 flex-1">
                    {line.displayText.slice(1)}
                    {cursor}
                  </span>
                </p>
              );
            })}

            {/* Interaktive Eingabe nach Boot */}
            {done && (
              <div className="mt-3 flex items-center text-sm sm:text-sm">
                <span className="text-[#AC8E66]">$</span>
                {inputValue
                  ? <>
                      <span className="ml-1 text-[#EFEBDC] whitespace-pre">{inputValue}</span>
                      <span className="inline-block w-[2px] h-[1.1em] bg-[#AC8E66] animate-cursor-blink flex-none" />
                    </>
                  : <>
                      <span className="inline-block w-[2px] h-[1.1em] bg-[#AC8E66] ml-1 animate-cursor-blink flex-none" />
                      <span className="text-[#EFEBDC]/40 text-[10px] whitespace-pre ml-1">tippe help · ls · cd</span>
                    </>
                }
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="sr-only"
                  spellCheck={false}
                  autoComplete="off"
                />
              </div>
            )}
          </div>

          {/* Shortcut Bar */}
          {done && (
            <div className="flex items-center gap-3 px-4 sm:px-6 py-2.5 bg-[#1a1a1a] border-t border-[#AC8E66]/10">
              {SHORTCUTS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => handleShortcut(s)}
                  className="flex items-center gap-1.5 text-xs text-[#EFEBDC]/50 hover:text-[#EFEBDC]/80 transition-colors cursor-pointer"
                >
                  <kbd className="inline-flex items-center justify-center w-5 h-5 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] font-mono leading-none">
                    {s.key}
                  </kbd>
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotFound;
